import type { CSSProperties, FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

interface ClassNameProps {
    className?: string
}

/** Two faint dashed vertical lines running along the content edges of a dark band. */
export const GuideLines: FC<ClassNameProps> = ({ className }): ReactNode => {
    return (
        <div aria-hidden='true' className={cn('pointer-events-none absolute inset-0', className)}>
            <div className='shell relative h-full'>
                <span className='absolute inset-y-0 left-2 border-l border-dashed border-white/[0.09] sm:left-4 lg:left-6' />
                <span className='absolute inset-y-0 right-2 border-r border-dashed border-white/[0.09] sm:right-4 lg:right-6' />
            </div>
        </div>
    )
}

/** Barely visible technical-drawing grid with dotted arcs and empty circles. */
export const Blueprint: FC<ClassNameProps> = ({ className }): ReactNode => {
    return (
        <div aria-hidden='true' className={cn('pointer-events-none absolute', className)}>
            <div className='absolute inset-0 bg-blueprint [mask-image:radial-gradient(ellipse_at_center,black_18%,transparent_72%)]' />

            <svg
                viewBox='0 0 600 300'
                preserveAspectRatio='xMidYMax meet'
                fill='none'
                className='absolute inset-x-0 bottom-0 h-1/2 w-full'
            >
                <path d='M 30 300 A 270 270 0 0 1 570 300' stroke='rgba(255,255,255,0.13)' strokeDasharray='1.5 7' strokeLinecap='round' />
                <path d='M 110 300 A 190 190 0 0 1 490 300' stroke='rgba(255,255,255,0.1)' strokeDasharray='1.5 7' strokeLinecap='round' />
                <path d='M 190 300 A 110 110 0 0 1 410 300' stroke='rgba(255,255,255,0.08)' strokeDasharray='1.5 7' strokeLinecap='round' />
                <circle cx='70' cy='212' r='6' stroke='rgba(255,255,255,0.16)' />
                <circle cx='300' cy='118' r='4.5' stroke='rgba(255,255,255,0.14)' />
                <circle cx='522' cy='188' r='7' stroke='rgba(255,255,255,0.14)' />
                <circle cx='412' cy='256' r='3.5' stroke='rgba(255,255,255,0.18)' />
                <circle cx='168' cy='270' r='3' stroke='rgba(255,255,255,0.14)' />
            </svg>
        </div>
    )
}

interface DarkBandProps {
    children: ReactNode
    id?: string
    className?: string
    rise?: boolean
    'aria-labelledby'?: string
    'aria-label'?: string
}

const RISE_STYLE: CSSProperties = {
    transform: 'translate3d(0, calc(var(--rise, 0) * 140px), 0) scale(calc(1 - var(--rise, 0) * 0.045))',
    transformOrigin: 'top center'
}

/**
 * Full-bleed warm charcoal sheet with large rounded top corners. As it scrolls
 * into view it rises over the cream page. Follow it with <CreamSheet> to bring
 * the light page back over its lower edge.
 */
export const DarkBand: FC<DarkBandProps> = ({
    children,
    id,
    className,
    rise = true,
    ...rest
}): ReactNode => {
    const ref = useRef<HTMLElement>(null)
    const reduced = useReducedMotion()

    useEffect(() => {
        const element = ref.current

        if (!element || !rise) return

        if (reduced) {
            element.style.setProperty('--rise', '0')
            return
        }

        let frame = 0

        const update = () => {
            frame = 0

            const rect = element.getBoundingClientRect()
            const viewport = window.innerHeight || 1
            const progress = Math.min(1, Math.max(0, (viewport - rect.top) / (viewport * 0.7)))
            const eased = 1 - Math.pow(1 - progress, 3)

            element.style.setProperty('--rise', (1 - eased).toFixed(4))
        }

        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', schedule, { passive: true })
        window.addEventListener('resize', schedule)

        return () => {
            cancelAnimationFrame(frame)
            window.removeEventListener('scroll', schedule)
            window.removeEventListener('resize', schedule)
        }
    }, [rise, reduced])

    return (
        <section
            ref={ref}
            id={id}
            data-header-theme='dark'
            style={rise ? RISE_STYLE : undefined}
            className={cn(
                'relative z-10 overflow-hidden rounded-t-[32px] bg-charcoal text-white sm:rounded-t-[48px] lg:rounded-t-[64px]',
                className
            )}
            {...rest}
        >
            <GuideLines />
            {children}
        </section>
    )
}

interface CreamSheetProps {
    children: ReactNode
    id?: string
    className?: string
    'aria-labelledby'?: string
    'aria-label'?: string
}

/** Light section that slides back over the bottom edge of a dark band. */
export const CreamSheet: FC<CreamSheetProps> = ({ children, id, className, ...rest }): ReactNode => {
    return (
        <section
            id={id}
            data-header-theme='light'
            className={cn(
                'relative z-20 -mt-10 rounded-t-[32px] bg-cream sm:-mt-12 sm:rounded-t-[48px] lg:-mt-16 lg:rounded-t-[64px]',
                className
            )}
            {...rest}
        >
            {children}
        </section>
    )
}
